import { Avatar, Segmented } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFromStorage } from 'src/lib/storage';
import { editPromptAction } from 'src/modules/SuperAdmin/store/actions/prompts.action';
import { getPromptsByIdSlice } from 'src/modules/SuperAdmin/store/slices/prompts.slice';
import * as Yup from 'yup';
import { SuperAdminModal } from '../../../baseComponents';
import './Prompt.scss';

const EditPromptPage = ({
  isOpen,
  setIsOpen,
  selectedSpecialty,
  selectedCondition,
}) => {
  const dispatch = useDispatch();
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );

  const cachedUserEmail = getFromStorage(`user_email`);
  const { getPromptById, getLlMVersionsList, promptScreens } = useSelector(
    (state) => state?.promptsState
  );
  const { getAllConditionsList } = useSelector(
    (state) => state?.conditionState
  );
  const [llmMaxTokenLimits, setLlmMaxTokenLimits] = useState({});
  const [llmVersionOptions, setLlmVersionOptions] = useState([]);
  const [llmVariables, setLlmVariables] = useState([]);

  useEffect(() => {
    if (getAllConditionsList && Array.isArray(getAllConditionsList)) {
      const dataArr = getAllConditionsList.map((condition) => ({
        value: condition.condition_id,
        label: condition.condition_name ? condition.condition_name : 'N/A',
      }));
      setConditionOptions(dataArr);
    }
  }, [getAllConditionsList]);

  useEffect(() => {
    if (getLlMVersionsList && Array.isArray(getLlMVersionsList)) {
      let dataArr = [];
      let maxTokens = {};
      getLlMVersionsList.forEach((key, index) => {
        dataArr.push({
          value: key?.llm_version,
          label: key?.model ? key?.model : 'N/A',
          name: key?.max_tokens,
        });
        maxTokens[key?.llm_version] = key?.max_tokens;
      });
      setLlmVersionOptions(dataArr);
      setLlmMaxTokenLimits(maxTokens);
    }
  }, [getLlMVersionsList]);

  useEffect(() => {
    if (
      getPromptById?.LLM_variables &&
      Array.isArray(getPromptById?.LLM_variables)
    ) {
      let dataArr = [];
      getPromptById?.LLM_variables.forEach((key, index) => {
        dataArr.push({
          llm_id: key?.llm_id ? key?.llm_id : '',
          prompt_screen: key?.prompt_screen ? key?.prompt_screen : '',
          max_tokens: key?.max_tokens ? key?.max_tokens : '',
          temp: key?.temp ? key?.temp : '',
          top_p: key?.top_p ? key?.top_p : '',
          created_at: key?.created_at ? key?.created_at : '',
          created_by: key?.created_by ? key?.created_by : '',
          description: key?.description ? key?.description : '',
          feature_flags: key?.feature_flags ? key?.feature_flags : '',
          last_modified: key?.last_modified ? key?.last_modified : '',
          last_modified_by: key?.last_modified_by ? key?.last_modified_by : '',
          version: key?.version ? key?.version : '',
        });
      });
      setLlmVariables(dataArr);
    }
  }, [getPromptById?.LLM_variables]);

  const activeLLM = (button) =>
    getPromptById?.LLM_variables?.find((variable) => {
      const normalizedPromptScreen = variable?.prompt_screen
        ?.toLowerCase()
        ?.replace(/_/g, ' ');
      return normalizedPromptScreen?.includes(button?.toLowerCase());
    });

  const [activeSegButton, setActiveSegButton] = useState('');

  useEffect(() => {
    if (isOpen) {
      const activeButtonDescription = activeLLM('chat')?.description;
      setActiveSegButton(activeButtonDescription);
    }
  }, [isOpen]);

  const [conditionOptions, setConditionOptions] = useState([]);

  const [promptScreenOptions, setPromptScreenOptions] = useState([]);
  // Responses segmented buttons : Start
  const [segmentedButtons, setSegmentedButtons] = useState([]);
  const [activeButton, setActiveButton] = useState(null);
  const [activeContent, setActiveContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [updatedPromptPayload, setUpdatedPromptPayload] = useState([]);
  // End

  // Prefix_suffix segmented button : Start
  const [staticSegmentedButtons, setStaticSegmentedButtons] = useState([]);
  const [staticActiveButton, setStaticActiveButton] = useState(null);
  const [staticActiveContent, setStaticActiveContent] = useState('');
  const [staticOriginalContent, setStaticOriginalContent] = useState('');
  const [updatedStaticPromptPayload, setUpdatedStaticPromptPayload] = useState(
    []
  );
  const [removedIndices, setRemovedIndices] = useState([]);
  const [removedByMatchIndices, setRemovedByMatchIndices] = useState([]);
  // End

  const formatDate = (date) => {
    // Parse the date string
    const convertedDate = moment(date, 'ddd, DD MMM YYYY HH:mm:ss [GMT]');

    // Format the date as "4th June 2024"
    const formattedDate = convertedDate?.format('Do MMMM YYYY');
    return formattedDate;
  };

  const initialValues = {
    condition_id: getPromptById?.prompt?.condition_id,
    condition_name: getPromptById?.prompt?.condition_name,
    description: getPromptById?.description,
    lumi_description: activeLLM('chat')?.description
      ? activeLLM('chat')?.description
      : null,
    intake_description: activeLLM('intake summary')?.description
      ? activeLLM('intake summary')?.description
      : null,
    prompt_name: getPromptById?.prompt?.prompt,
    prompt_screen: getPromptById?.prompt_screen,
    lumi_prompt_screen: activeLLM('chat')?.prompt_screen
      ? activeLLM('chat')?.prompt_screen
      : null,
    intake_prompt_screen: activeLLM('intake summary')?.prompt_screen
      ? activeLLM('intake summary')?.prompt_screen
      : null,
    llm_version: activeLLM(getPromptById?.prompt_screen)?.llm_id
      ? activeLLM(getPromptById?.prompt_screen)?.llm_id
      : getPromptById?.prompt?.llm_id
        ? getPromptById?.prompt?.llm_id
        : null,
    lumi_llm_version: activeLLM('chat')?.llm_id
      ? activeLLM('chat')?.llm_id
      : null,
    intake_llm_version: activeLLM('intake summary')?.llm_id
      ? activeLLM('intake summary')?.llm_id
      : null,
    lumi_max_tokens: activeLLM('chat')?.max_tokens || null,
    intake_max_tokens: activeLLM('intake summary')?.max_tokens || null,
    lumi_num_questions: activeLLM('chat')?.num_questions ?? null,
    lumi_temp: activeLLM('chat')?.temp ? activeLLM('chat')?.temp : null,
    intake_temp: activeLLM('intake summary')?.temp
      ? activeLLM('intake summary')?.temp
      : null,
    lumi_top_p: activeLLM('chat')?.top_p ? activeLLM('chat')?.top_p : null,
    intake_top_p: activeLLM('intake summary')?.top_p
      ? activeLLM('intake summary')?.top_p
      : null,
    max_tokens: activeLLM(getPromptById?.prompt_screen)?.max_tokens
      ? activeLLM(getPromptById?.prompt_screen)?.max_tokens
      : getPromptById?.prompt?.max_token
        ? getPromptById?.prompt?.max_token
        : null,
    temp: activeLLM(getPromptById?.prompt_screen)?.temp
      ? activeLLM(getPromptById?.prompt_screen)?.temp
      : getPromptById?.prompt?.temp
        ? getPromptById?.prompt?.temp
        : null,
    top_p: activeLLM(getPromptById?.prompt_screen)?.top_p
      ? activeLLM(getPromptById?.prompt_screen)?.top_p
      : getPromptById?.prompt?.top_p
        ? getPromptById?.prompt?.top_p
        : null,
    feature_flags: getPromptById?.prompt?.feature_flags
      ? getPromptById?.prompt?.feature_flags
      : '{}',
    lumi_feature_flags: activeLLM('chat')?.feature_flags
      ? activeLLM('chat')?.feature_flags
      : null,
    intake_feature_flags: activeLLM('intake summary')?.feature_flags
      ? activeLLM('intake summary')?.feature_flags
      : null,
    last_modified: getPromptById?.prompt?.last_modified
      ? formatDate(getPromptById?.prompt?.last_modified)
      : formatDate(getPromptById?.prompt?.created_at),
    lumi_last_modified: activeLLM('chat')?.last_modified
      ? formatDate(activeLLM('chat')?.last_modified)
      : formatDate(activeLLM('chat')?.created_at),
    intake_last_modified: activeLLM('intake summary')?.last_modified
      ? formatDate(activeLLM('intake summary')?.last_modified)
      : formatDate(activeLLM('intake summary')?.created_at),
    last_modified_by: getPromptById?.prompt?.last_modified_by
      ? getPromptById?.prompt?.last_modified_by
      : getPromptById?.prompt?.created_by,
    lumi_last_modified_by: activeLLM('chat')?.last_modified_by
      ? activeLLM('chat')?.last_modified_by
      : activeLLM('chat')?.created_by,
    intake_last_modified_by: activeLLM('intake summary')?.last_modified_by
      ? activeLLM('intake summary')?.last_modified_by
      : activeLLM('intake summary')?.created_by,
    version: getPromptById?.prompt?.version,
    lumi_version: activeLLM('chat')?.version
      ? activeLLM('chat')?.version
      : null,
    intake_version: activeLLM('intake summary')?.version
      ? activeLLM('intake summary')?.version
      : null,
    lumi_tools: activeLLM('chat')?.tools ? activeLLM('chat')?.tools : null,
  };

  const handleSegmented = (value) => {
    setActiveSegButton(value);
  };

  // Auto generated bg color for segmented buttons : Start
  const generateLighterColor = (color) => {
    const r = parseInt(color.substring(1, 3), 16);
    const g = parseInt(color.substring(3, 5), 16);
    const b = parseInt(color.substring(5, 7), 16);

    const lighterR = Math.round((r + 255) / 2.2);
    const lighterG = Math.round((g + 255) / 2.2);
    const lighterB = Math.round((b + 255) / 2.2);

    const lighterColor =
      '#' +
      lighterR.toString(16).padStart(2, '0') +
      lighterG.toString(16).padStart(2, '0') +
      lighterB.toString(16).padStart(2, '0');

    return lighterColor;
  };

  const hashCode = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return hash;
  };

  const intToRGB = (i) => {
    const c = (i & 0x00ffffff).toString(16).toUpperCase();
    return '00000'.substring(0, 6 - c.length) + c;
  };

  function stringToColor(str) {
    const hash = hashCode(str);
    const rgb = intToRGB(hash);
    const color = `#${rgb}`;
    return generateLighterColor(color);
  }
  // Auto generated bg color for segmented buttons : End

  useEffect(() => {
    if (
      getPromptById?.responses &&
      Array.isArray(getPromptById?.responses) &&
      getPromptById?.responses.length > 0 &&
      getPromptById?.prefix_suffix &&
      Array.isArray(getPromptById?.prefix_suffix)
    ) {
      // Convert prefix_suffix to a Set for efficient lookup
      const prefixSuffixSet = new Set(
        getPromptById.prefix_suffix.map((item) => JSON.stringify(item))
      );

      // Capture indices of responses to be removed based on matches with description and prompt details
      const removedByMatchIndices = getPromptById.responses
        .map((response, index) => ({
          response,
          originalIndex: index + 1,
        }))
        .filter(
          ({ response }) =>
            response.prompt_id === getPromptById.prompt?.prompt_id
        )
        .map(({ originalIndex }) => originalIndex);

      // Set indices captured by match state
      setRemovedByMatchIndices(removedByMatchIndices);

      // Filter responses to remove those that are also in prefix_suffix and those matching the description and prompt details
      const filteredResponses = getPromptById.responses
        .map((response, index) => ({
          response,
          originalIndex: index + 1, // Store original index, adjusting to 1-based index
        }))
        .filter(
          ({ response, originalIndex }) =>
            !prefixSuffixSet.has(JSON.stringify(response)) &&
            !removedByMatchIndices.includes(originalIndex)
        );

      // Determine removed indices for prefix_suffix
      const removedIndices = getPromptById.responses
        .map((response, index) => ({
          response,
          originalIndex: index + 1,
        }))
        .filter(({ response }) => prefixSuffixSet.has(JSON.stringify(response)))
        .map(({ originalIndex }) => originalIndex);

      setRemovedIndices(removedIndices); // Set removed indices state

      const buttons = filteredResponses
        .map(({ response, originalIndex }) => {
          const filteredKeys = Object.keys(response).filter(
            (key) => key !== 'prompt_id' && key !== 'prompt_screens_id'
          );

          return filteredKeys.map((key) => {
            // Use prompt_id and prompt_screens_id to ensure uniqueness
            const uniqueKey = `${key}_${response.prompt_screens_id}_${response.prompt_id}`;
            const finalButtonNames = key.trim() === '' ? `No description` : key;

            return {
              label: finalButtonNames,
              value: uniqueKey,
              content: response[key],
              key: key,
              prompt_screen_id: response.prompt_screens_id,
              prompt_id: response.prompt_id,
              originalIndex, // Keep original index for avatar display
            };
          });
        })
        .filter(Boolean) // Remove nulls from filtering
        .flat();

      const buttonsWithAvatars = buttons.map((button) => {
        const dynamicBackgroundColor = stringToColor(
          `${button.originalIndex}_${button.value}`
        );

        return {
          ...button,
          label: (
            <div className="flex flex-col items-center">
              <Avatar
                size={24}
                className="text-sm flex items-center mt-1"
                style={{ backgroundColor: dynamicBackgroundColor }}
              >
                <div style={{ fontSize: '10px' }}>{button.originalIndex}</div>{' '}
                {/* Use originalIndex */}
              </Avatar>
              <div className="mt-2">{button.label}</div>
            </div>
          ),
        };
      });

      setSegmentedButtons(buttonsWithAvatars);

      if (buttonsWithAvatars.length > 0) {
        setActiveButton(buttonsWithAvatars[0].value);
        setActiveContent(buttonsWithAvatars[0].content);
        setOriginalContent(buttonsWithAvatars[0].content);
      }
    }
  }, [getPromptById]);

  // Handle static data in a separate useEffect
  useEffect(() => {
    if (
      getPromptById?.prefix_suffix &&
      Array.isArray(getPromptById?.prefix_suffix) &&
      getPromptById?.prefix_suffix.length > 0
    ) {
      const buttons = getPromptById.prefix_suffix
        .flatMap((item, originalIndex) => {
          const filteredKeys = Object.keys(item).filter(
            (key) => key !== 'prompt_id' && key !== 'prompt_screens_id'
          );

          return filteredKeys.map((key) => {
            // Use prompt_id and prompt_screens_id to ensure uniqueness
            const uniqueKey = `${key}_${item.prompt_screens_id}_${item.prompt_id}`;
            const finalButtonNames = key.trim() === '' ? `No description` : key;

            return {
              label: finalButtonNames,
              value: uniqueKey,
              content: item[key],
              key: key,
              prompt_screen_id: item.prompt_screens_id,
              prompt_id: item.prompt_id,
              originalIndex, // Track the original index
              missingButton: removedIndices[originalIndex] ?? originalIndex + 1,
            };
          });
        })
        .filter(Boolean); // Remove nulls from filtering

      const buttonsWithAvatars = buttons.map((button) => {
        const dynamicBackgroundColor = stringToColor(
          `${button.originalIndex + 1}_${button.value}`
        );
        return {
          ...button,
          label: (
            <div className="flex flex-col items-center">
              <Avatar
                size={24}
                className="text-sm flex items-center mt-1"
                style={{ backgroundColor: dynamicBackgroundColor }}
              >
                <div style={{ fontSize: '10px' }}>{button.missingButton}</div>
              </Avatar>
              <div className="mt-2">{button.label}</div>
            </div>
          ),
        };
      });

      setStaticSegmentedButtons(buttonsWithAvatars);

      if (buttonsWithAvatars.length > 0) {
        setStaticActiveButton(buttonsWithAvatars[0].value);
        setStaticActiveContent(buttonsWithAvatars[0].content);
        setStaticOriginalContent(buttonsWithAvatars[0].content);
      }
    }
  }, [getPromptById, removedIndices]);

  useEffect(() => {
    if (promptScreens && Array.isArray(promptScreens)) {
      let dataArr = [];
      promptScreens.forEach((key, index) => {
        dataArr.push({
          value: key?.prompt_screens_id,
          label: key?.prompt_screen,
          description: key?.description,
        });
      });
      setPromptScreenOptions(dataArr);
    }
  }, [promptScreens]);

  const handleSegmentChange = (value, isStatic = false) => {
    const buttons = isStatic ? staticSegmentedButtons : segmentedButtons;
    const selectedButton = buttons.find((button) => button.value === value);
    setActiveButton(value);
    value === 'intake_summary'
      ? setActiveContent(
          updatedPromptPayload?.length > 0
            ? updatedPromptPayload[0]?.intake_summary
            : selectedButton
              ? selectedButton.content
              : ''
        )
      : setActiveContent(selectedButton ? selectedButton.content : '');
    setOriginalContent(selectedButton ? selectedButton.content : '');
  };

  const handleContentChange = (e) => {
    const newValue = e.target.value;
    setActiveContent(newValue);

    // Check if the content is different from the original content
    if (newValue !== originalContent) {
      const selectedButton = segmentedButtons.find(
        (button) => button.value === activeButton
      );
      // Payload set here  : Starts
      if (selectedButton) {
        setUpdatedPromptPayload([
          {
            [selectedButton.key]: newValue,
            prompt_screens_id: selectedButton.prompt_screen_id,
          },
        ]);
      }
    } else {
      setUpdatedPromptPayload([]);
    }
    // Ends
  };

  const customScrollbarStyle = {
    overflowY: 'hidden',
    overflowX: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: '#ccc transparent',
    whiteSpace: 'nowrap',
  };

  const handleStaticSegmentChange = (value) => {
    const selectedButton = staticSegmentedButtons.find(
      (button) => button.value === value
    );
    setStaticActiveButton(value);
    if (
      value.startsWith('Intake summary prompt') &&
      !value.includes('suffix') &&
      !value.includes('prefix')
    ) {
      if (updatedStaticPromptPayload.length > 0) {
        const updatedContent =
          updatedStaticPromptPayload[0]['Intake summary prompt'];
        setStaticActiveContent(updatedContent || '');
      } else {
        setStaticActiveContent(selectedButton.content);
      }
    } else {
      setStaticActiveContent(selectedButton ? selectedButton.content : '');
    }
    setStaticOriginalContent(selectedButton ? selectedButton.content : '');
  };

  const handleStaticContentChange = (e) => {
    const newValue = e.target.value;
    setStaticActiveContent(newValue);

    if (newValue !== staticOriginalContent) {
      const selectedButton = staticSegmentedButtons.find(
        (button) => button.value === staticActiveButton
      );
      if (selectedButton) {
        setUpdatedStaticPromptPayload([
          {
            [selectedButton.key]: newValue,
            prompt_screens_id: selectedButton.prompt_screen_id,
          },
        ]);
      }
    } else {
      setUpdatedStaticPromptPayload([]);
    }
  };

  const featureFlagsString =
    activeSegButton === activeLLM('chat')?.description &&
    llmVariables?.length === 2
      ? initialValues.lumi_feature_flags
      : activeSegButton === activeLLM('intake summary')?.description &&
          llmVariables?.length === 2
        ? initialValues.intake_feature_flags
        : llmVariables?.length === 1 &&
          activeLLM(getPromptById?.prompt_screen)?.feature_flags;

  const toolsString =
    activeSegButton === activeLLM('chat')?.description &&
    llmVariables?.length === 1 &&
    llmVariables[0]?.prompt_screen === activeLLM('chat')?.prompt_screen
      ? initialValues.lumi_tools
      : null;

  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (isOpen && scrollContainerRef.current) {
      setTimeout(() => {
        scrollContainerRef.current.scrollLeft = 0;
      }, 100);
    }
  }, [isOpen]);

  const PromptsSegment = [
    {
      label: activeLLM('chat')?.description,
      isActive: activeButton === activeLLM('chat')?.description,
      onClick: () => setActiveSegButton(activeLLM('chat')?.description),
    },
    {
      label: activeLLM('intake summary')?.description,
      isActive: activeButton === activeLLM('intake summary')?.description,
      onClick: () =>
        setActiveSegButton(activeLLM('intake summary')?.description),
    },
  ];

  const sections = [
    {
      heading: (
        <>
          <div className="">
            Edit prompt
            {getPromptById?.label === 'global' ? (
              <span className="mx-1 text-[#16a878] rounded-full bg-[#eefaf4] justify-center text-[15px] font-medium my-0.5 px-2">
                Global
              </span>
            ) : (
              ''
            )}
          </div>
        </>
      ),
      className: ' flex gap-6 items-center ',
      fields: [
        {
          name: 'sections',
          type: 'label',
          title: (
            <div>
              <div>
                {getPromptById?.LLM_variables?.length > 1 && (
                  <div className="flex -mb-2 py-2 ">
                    <div className="py-1 text-base">Prompt metadata :</div>
                    <Segmented
                      options={PromptsSegment.map((button) => ({
                        label: button.label,
                        value: button.label,
                      }))}
                      value={activeSegButton}
                      onChange={(value) => handleSegmented(value)}
                    />
                  </div>
                )}
              </div>
            </div>
          ),
        },
      ],
    },
    {
      heading: (
        <>
          <div className="flex gap-5">
            <div className="flex gap-1">
              <div className="font-bold text-sm">Condition name : </div>
              <div className="font-normal text-sm">
                {initialValues.condition_name || ''}
              </div>
            </div>
            <div className="flex gap-1">
              <div className="font-bold text-sm">Prompt screen : </div>
              <div className="font-normal text-sm">
                {' '}
                {activeSegButton === activeLLM('chat')?.description &&
                llmVariables?.length === 2
                  ? initialValues.lumi_prompt_screen
                  : activeSegButton ===
                        activeLLM('intake summary')?.description &&
                      llmVariables?.length === 2
                    ? initialValues.intake_prompt_screen
                    : (llmVariables?.length === 1 &&
                        activeLLM(getPromptById?.prompt_screen)
                          ?.prompt_screen) ||
                      ''}
              </div>
            </div>
            <div className="flex gap-1">
              <div className="font-bold text-sm">Description : </div>
              <div className="font-normal text-sm">
                {activeSegButton === activeLLM('chat')?.description &&
                llmVariables?.length === 2
                  ? initialValues.lumi_description
                  : activeSegButton ===
                        activeLLM('intake summary')?.description &&
                      llmVariables?.length === 2
                    ? initialValues.intake_description
                    : llmVariables?.length === 1 &&
                      activeLLM(getPromptById?.prompt_screen)?.description}
              </div>
            </div>
          </div>
        </>
      ),
      fields: [],
    },
    {
      heading: (
        <>
          <div className="flex gap-5">
            <div className="flex gap-1">
              <div className="font-bold text-sm">Version : </div>
              <div className="font-normal text-sm">
                {activeSegButton === activeLLM('chat')?.description &&
                llmVariables?.length === 2
                  ? initialValues.lumi_version
                  : activeSegButton ===
                        activeLLM('intake summary')?.description &&
                      llmVariables?.length === 2
                    ? initialValues.intake_version
                    : (llmVariables?.length === 1 &&
                        activeLLM(getPromptById?.prompt_screen)?.version) ||
                      ''}
              </div>
            </div>
            <div className="flex gap-1">
              <div className="font-bold text-sm">Last modified :</div>
              <div className="font-normal text-sm">
                {' '}
                {activeSegButton === activeLLM('chat')?.description &&
                llmVariables?.length === 2
                  ? initialValues.lumi_last_modified
                  : activeSegButton ===
                        activeLLM('intake summary')?.description &&
                      llmVariables?.length === 2
                    ? initialValues.intake_last_modified
                    : (llmVariables?.length === 1 &&
                        (activeLLM(getPromptById?.prompt_screen)?.last_modified
                          ? formatDate(
                              activeLLM(getPromptById?.prompt_screen)
                                ?.last_modified
                            )
                          : formatDate(
                              activeLLM(getPromptById?.prompt_screen)
                                ?.created_at
                            ))) ||
                      ''}
              </div>
            </div>
            <div className="flex gap-1">
              <div className="font-bold text-sm">Last modified by : </div>
              <div className="font-normal text-sm">
                {activeSegButton === activeLLM('chat')?.description &&
                llmVariables?.length === 2
                  ? initialValues.lumi_last_modified_by
                  : activeSegButton ===
                        activeLLM('intake summary')?.description &&
                      llmVariables?.length === 2
                    ? initialValues.intake_last_modified_by
                    : llmVariables?.length === 1 &&
                        activeLLM(getPromptById?.prompt_screen)
                          ?.last_modified_by
                      ? activeLLM(getPromptById?.prompt_screen)
                          ?.last_modified_by
                      : activeLLM(getPromptById?.prompt_screen)?.created_by ||
                        ''}
              </div>
            </div>
          </div>
        </>
      ),
      fields: [],
    },
    {
      heading: (
        <div className="flex gap-1">
          <div className="font-bold text-sm">Feature flags : </div>
          <div className="font-normal text-sm">{featureFlagsString}</div>
        </div>
      ),
      fields: [],
    },
    {
      heading: (
        <div className="flex gap-1">
          <div className="font-bold text-sm">Tools : </div>
          <div className="font-normal text-sm">
            {toolsString || 'Not Applicable'}
          </div>
        </div>
      ),
      fields: [],
    },
    {
      className:
        (activeSegButton === activeLLM('chat')?.description &&
          llmVariables?.length === 2) ||
        (llmVariables?.length === 1 &&
          llmVariables[0]?.prompt_screen === activeLLM('chat')?.prompt_screen)
          ? 'mt-6 grid grid-cols-5 gap-3'
          : 'mt-6 grid grid-cols-4 gap-4',
      fields: [
        {
          name:
            activeSegButton === activeLLM('chat')?.description &&
            llmVariables?.length === 2
              ? 'lumi_llm_version'
              : activeSegButton === activeLLM('intake summary')?.description &&
                  llmVariables?.length === 2
                ? 'intake_llm_version'
                : llmVariables?.length === 1 &&
                    llmVariables[0]?.prompt_screen ===
                      activeLLM('chat')?.prompt_screen
                  ? 'lumi_llm_version'
                  : 'intake_llm_version',
          type: 'select',
          title: 'LLM version',
          placeholder: 'Select LLM version',
          options: llmVersionOptions,
          onChange: (setFieldValue, e) => {
            if (
              activeSegButton === activeLLM('chat')?.description &&
              llmVariables?.length === 2
            ) {
              setFieldValue('lumi_llm_version', e);
            } else if (
              activeSegButton === activeLLM('intake summary')?.description
            ) {
              setFieldValue('intake_llm_version', e);
            } else if (
              llmVariables?.length === 1 &&
              llmVariables[0]?.prompt_screen ===
                activeLLM('chat')?.prompt_screen
            ) {
              setFieldValue('lumi_llm_version', e);
            } else if (
              llmVariables?.length === 1 &&
              llmVariables[0]?.prompt_screen ===
                activeLLM('intake summary')?.prompt_screen
            ) {
              setFieldValue('intake_llm_version', e);
            }
          },
        },
        {
          name:
            activeSegButton === activeLLM('chat')?.description &&
            llmVariables?.length === 2
              ? 'lumi_max_tokens'
              : activeSegButton === activeLLM('intake summary')?.description &&
                  llmVariables?.length === 2
                ? 'intake_max_tokens'
                : llmVariables?.length === 1 &&
                    llmVariables[0]?.prompt_screen ===
                      activeLLM('chat')?.prompt_screen
                  ? 'lumi_max_tokens'
                  : 'intake_max_tokens',
          type: 'InputNumber',
          title: 'Max tokens',
          placeholder: 'Enter max tokens',
          onChange: (setFieldValue, e) => {
            if (
              activeSegButton === activeLLM('chat')?.description &&
              llmVariables?.length === 2
            ) {
              setFieldValue('lumi_max_tokens', e);
            } else if (
              activeSegButton === activeLLM('intake summary')?.description &&
              llmVariables?.length === 2
            ) {
              setFieldValue('intake_max_tokens', e);
            } else if (
              llmVariables?.length === 1 &&
              llmVariables[0]?.prompt_screen ===
                activeLLM('chat')?.prompt_screen
            ) {
              setFieldValue('lumi_max_tokens', e);
            } else if (
              llmVariables?.length === 1 &&
              llmVariables[0]?.prompt_screen ===
                activeLLM('intake summary')?.prompt_screen
            ) {
              setFieldValue('intake_max_tokens', e);
            }
          },
        },
        {
          name:
            activeSegButton === activeLLM('chat')?.description &&
            llmVariables?.length === 2
              ? 'lumi_temp'
              : activeSegButton === activeLLM('intake summary')?.description &&
                  llmVariables?.length === 2
                ? 'intake_temp'
                : llmVariables?.length === 1 &&
                    llmVariables[0]?.prompt_screen ===
                      activeLLM('chat')?.prompt_screen
                  ? 'lumi_temp'
                  : 'intake_temp',
          type: 'text',
          title: 'Temp',
          placeholder: 'Enter temp',
          className: 'col-span-1',
          onChange: (setFieldValue, e) => {
            if (
              activeSegButton === activeLLM('chat')?.description &&
              llmVariables?.length === 2
            ) {
              setFieldValue('lumi_temp', e.target.value);
            } else if (
              activeSegButton === activeLLM('intake summary')?.description &&
              llmVariables?.length === 2
            ) {
              setFieldValue('intake_temp', e.target.value);
            } else if (
              llmVariables?.length === 1 &&
              llmVariables[0]?.prompt_screen ===
                activeLLM('chat')?.prompt_screen
            ) {
              setFieldValue('lumi_temp', e.target.value);
            } else if (
              llmVariables?.length === 1 &&
              llmVariables[0]?.prompt_screen ===
                activeLLM('intake summary')?.prompt_screen
            ) {
              setFieldValue('intake_temp', e.target.value);
            }
          },
        },
        {
          name:
            activeSegButton === activeLLM('chat')?.description &&
            llmVariables?.length === 2
              ? 'lumi_top_p'
              : activeSegButton === activeLLM('intake summary')?.description &&
                  llmVariables?.length === 2
                ? 'intake_top_p'
                : llmVariables?.length === 1 &&
                    llmVariables[0]?.prompt_screen ===
                      activeLLM('chat')?.prompt_screen
                  ? 'lumi_top_p'
                  : 'intake_top_p',
          type: 'InputNumber',
          title: 'Top_p',
          placeholder: 'Enter top_p',
          onChange: (setFieldValue, e) => {
            if (
              activeSegButton === activeLLM('chat')?.description &&
              llmVariables?.length === 2
            ) {
              setFieldValue('lumi_top_p', e);
            } else if (
              activeSegButton === activeLLM('intake summary')?.description &&
              llmVariables?.length === 2
            ) {
              setFieldValue('intake_top_p', e);
            } else if (
              llmVariables?.length === 1 &&
              llmVariables[0]?.prompt_screen ===
                activeLLM('chat')?.prompt_screen
            ) {
              setFieldValue('lumi_top_p', e);
            } else if (
              llmVariables?.length === 1 &&
              llmVariables[0]?.prompt_screen ===
                activeLLM('intake summary')?.prompt_screen
            ) {
              setFieldValue('intake_top_p', e);
            }
          },
          className: 'col-span-1',
        },
        ...((activeSegButton === activeLLM('chat')?.description &&
          llmVariables?.length === 2) ||
        (llmVariables?.length === 1 &&
          llmVariables[0]?.prompt_screen === activeLLM('chat')?.prompt_screen)
          ? [
              {
                name: 'lumi_num_questions',
                type: 'InputNumber',
                title: 'Num_questions',
                placeholder: 'Enter num_questions',
                onChange: (setFieldValue, e) => {
                  setFieldValue('lumi_num_questions', e);
                },
              },
            ]
          : []),
      ],
    },
    {
      className: 'mt-6',
      fields: [
        {
          name: 'lumi_tools',
          type: 'modalTextArea',
          title: 'Tools (JSON format)',
          placeholder: 'Enter tools configuration as JSON string',
          onChange: (setFieldValue, e) => {
            if (
              activeSegButton === activeLLM('chat')?.description &&
              llmVariables?.length === 2
            ) {
              setFieldValue('lumi_tools', e.target.value);
            } else if (
              llmVariables?.length === 1 &&
              llmVariables[0]?.prompt_screen ===
                activeLLM('chat')?.prompt_screen
            ) {
              setFieldValue('lumi_tools', e.target.value);
            }
          },
          autoSize: { minRows: 1, maxRows: 1 },
          hidden: !(
            (activeSegButton === activeLLM('chat')?.description &&
              llmVariables?.length >= 1) ||
            (llmVariables?.length === 1 &&
              llmVariables[0]?.prompt_screen ===
                activeLLM('chat')?.prompt_screen)
          ),
        },
      ],
    },
    {
      className: '',
      fields: [
        {
          name: 'prompt_name',
          type: (() => {
            try {
              JSON.parse(initialValues.prompt_name);
              return 'keyValueTextArea';
            } catch (e) {
              return 'modalTextArea';
            }
          })(),
          title: (
            <>
              <div
                className={`flex items-center ${
                  initialValues?.description?.length >= 33 ? 'mt-6' : ''
                }`}
              >
                <div className="">{initialValues?.description || ''}</div>
                {removedByMatchIndices?.length > 0 &&
                  (segmentedButtons?.length > 0 ||
                    staticSegmentedButtons?.length > 0) && (
                    <div className="ml-2 flex">
                      <Avatar
                        size={24}
                        className="text-sm flex items-center mb-1 bg-[#00d091]"
                      >
                        <div style={{ fontSize: '10px' }}>
                          {' '}
                          {removedByMatchIndices[0]}
                        </div>
                      </Avatar>
                    </div>
                  )}
              </div>
            </>
          ),
          placeholder: 'Enter prompt',
        },
      ],
    },
    //  Static Segmented Tab : Starts
    {
      heading: staticSegmentedButtons?.length > 0 &&
        removedIndices?.length > 0 && (
          <div className="py-2">
            <div
              className="segmented_super_admin rounded-lg w-full mb-5 flex overflow-auto"
              style={{ ...customScrollbarStyle }}
            >
              <Segmented
                style={{ fontSize: '12px' }}
                options={staticSegmentedButtons}
                onChange={handleStaticSegmentChange}
                value={staticActiveButton}
              />
            </div>
            {staticActiveButton.startsWith('Intake summary prompt') && (
              <div className="text-xs ml-1 bg-[#00d091] w-fit rounded-full px-2 py-1 mb-2 text-[#fff]">
                This prompt is editable
              </div>
            )}
            <div>
              <TextArea
                className={`${
                  staticActiveButton.includes('suffix prompt') ||
                  staticActiveButton.includes('prefix prompt')
                    ? 'modal__form-el-field-textarea_reduced'
                    : 'modal__form-el-field-textarea'
                }${
                  staticActiveButton.startsWith('Intake summary prompt')
                    ? ' mt-2'
                    : ''
                }`}
                value={staticActiveContent ? staticActiveContent : ''}
                bordered={false}
                autoSize={{ minRows: 3, maxRows: 13 }}
                onChange={handleStaticContentChange}
                readOnly={
                  !staticActiveButton.startsWith('Intake summary prompt')
                }
                style={{ fontWeight: 400 }}
              />
            </div>
          </div>
        ),
      fields: [],
    },
    //  Ends

    //  Segmented Tab : Starts
    {
      heading: segmentedButtons?.length > 0 && (
        <div className="py-5">
          <div
            className="segmented_super_admin rounded-lg w-full mb-5 flex overflow-auto"
            style={{ ...customScrollbarStyle }}
            ref={scrollContainerRef}
          >
            <Segmented
              style={{ fontSize: '12px' }}
              options={segmentedButtons}
              onChange={handleSegmentChange}
              value={activeButton}
            />
          </div>
          <div>
            <TextArea
              className={`${
                activeButton.includes('suffix prompt') ||
                activeButton.includes('prefix prompt')
                  ? 'modal__form-el-field-textarea_reduced'
                  : 'modal__form-el-field-textarea'
              }${
                activeButton.startsWith('Intake summary prompt') ? ' mt-2' : ''
              }`}
              value={activeContent ? activeContent : ''}
              bordered={false}
              autoSize={{ minRows: 3, maxRows: 13 }}
              onChange={handleContentChange}
              readOnly
            />
          </div>
        </div>
      ),
      fields: [],
    },
  ];

  const llm_schema = (type, llmVariables, activeLLM, initialValues) => {
    const isLumi = (desc) => desc?.includes(activeLLM('chat')?.description);
    const isIntake = (desc) =>
      desc?.includes(activeLLM('intake summary')?.description);

    const getPrefix = () => {
      if (llmVariables?.length === 2) {
        return ['lumi', 'intake'];
      } else if (llmVariables?.length === 1) {
        return [isLumi(llmVariables[0]?.description) ? 'lumi' : 'intake'];
      }
      return ['intake'];
    };

    const prefixes = getPrefix();
    return prefixes.map(
      (prefix) => `${prefix}_${type === 'llm_id' ? 'llm_version' : type}`
    );
  };

  const createValidationSchema = (
    llmVariables,
    activeLLM,
    initialValues,
    llmMaxTokenLimits
  ) => {
    const schema = {};

    ['top_p', 'max_tokens', 'temp', 'llm_id'].forEach((type) => {
      const fields = llm_schema(type, llmVariables, activeLLM, initialValues);

      fields.forEach((field) => {
        if (type === 'top_p' || type === 'temp' || type === 'num_questions') {
          schema[field] = Yup.number()
            .typeError(
              `${type.charAt(0).toUpperCase() + type.slice(1)} must be a number`
            )
            .required(
              `${type.charAt(0).toUpperCase() + type.slice(1)} is required`
            )
            .min(
              0,
              `${
                type.charAt(0).toUpperCase() + type.slice(1)
              } must be at least 0`
            )
            .max(
              1,
              `${
                type.charAt(0).toUpperCase() + type.slice(1)
              } must be at most 1`
            )
            .test(`${field}-debug`, `Debugging ${field}`, function (value) {
              return true;
            });
        } else if (type === 'max_tokens') {
          schema[field] = Yup.number()
            .typeError('Max tokens must be a number')
            .required('Max tokens is required')
            .positive('Max tokens must be greater than 0')
            .integer('Max tokens must be an integer')
            .test(
              'max-token',
              'Max tokens exceeds the allowed limit',
              function (value) {
                const llm_id_field = field.startsWith('lumi')
                  ? 'lumi_llm_version'
                  : 'intake_llm_version';
                const llm_version = this?.parent[llm_id_field];
                const maxToken = llmMaxTokenLimits[llm_version];

                if (maxToken && value > maxToken) {
                  return this.createError({
                    message: `Max tokens exceeds the allowed limit of ${maxToken}`,
                    path: this.path,
                  });
                }

                return true;
              }
            );
        } else if (type === 'llm_id') {
          schema[field] = Yup.string()
            .required('LLM version is required')
            .test(`${field}-debug`, `Debugging ${field}`, function (value) {
              return true;
            });
        }
      });
    });

    // Add validation for tools fields
    ['lumi_tools'].forEach((field) => {
      // Only add validation if the field exists based on llmVariables
      const shouldValidate = llmVariables?.some((variable) =>
        variable?.description?.includes(activeLLM('chat')?.description)
      );

      if (shouldValidate || llmVariables?.length === 2) {
        schema[field] = Yup.string()
          .nullable() // Allow null or empty string
          .test(
            'is-json',
            'Tools must be a valid JSON string or empty',
            (value) => {
              if (!value) return true; // Allow empty or null
              try {
                JSON.parse(value);
                return true;
              } catch (e) {
                return false;
              }
            }
          )
          .test(`${field}-debug`, `Debugging ${field}`, function (value) {
            return true;
          });
      }
    });

    schema.prompt_name = Yup.string()
      .required(`${initialValues?.description} is required`)
      .test('prompt_name-debug', 'Debugging prompt_name', function (value) {
        return true;
      });

    return Yup.object().shape(schema);
  };

  const validationSchemaField = createValidationSchema(
    llmVariables,
    activeLLM,
    initialValues,
    llmMaxTokenLimits
  );

  return (
    <SuperAdminModal
      open={isOpen}
      initialValues={initialValues}
      validationSchema={validationSchemaField}
      submitButtonText="Submit"
      cancelButtonText="Cancel"
      centered={true}
      closable={true}
      sections={sections}
      className={`prompt-modal`}
      handleCancel={() => {
        setIsOpen(false);
        dispatch(getPromptsByIdSlice(null));
        setUpdatedPromptPayload([]);
        setUpdatedStaticPromptPayload([]);
        setSegmentedButtons([]);
        setRemovedIndices([]);
        setActiveSegButton(activeLLM('chat')?.description);
      }}
      handleSubmit={(values) => {
        const intakeVariables = activeLLM('intake summary');
        const llmVariables = activeLLM('chat');
        const payload = {
          condition_id: values?.condition_id,
          description: getPromptById?.description,
          prompt_screens_id: getPromptById?.prompt?.prompt_screens_id,
          prompt: values?.prompt_name,
          last_modified_by: cachedUserEmail,
          ...(updatedStaticPromptPayload.length > 0 && {
            edit_prompt_summary: updatedStaticPromptPayload,
          }),
          lumi_max_tokens: values?.lumi_max_tokens,
          lumi_llm_version: values?.lumi_llm_version,
          lumi_temp: values?.lumi_temp,
          lumi_top_p: values?.lumi_top_p,
          lumi_num_questions: values?.lumi_num_questions,
          intake_max_tokens: values?.intake_max_tokens,
          intake_llm_version: values?.intake_llm_version,
          intake_temp: values?.intake_temp,
          intake_top_p: values?.intake_top_p,
          intake_prompt_id: intakeVariables?.prompt_id
            ? intakeVariables?.prompt_id
            : null,
          lumi_prompt_id: llmVariables?.prompt_id
            ? llmVariables?.prompt_id
            : null,
          lumi_tools: values?.lumi_tools ? values.lumi_tools : null,
        };
        dispatch(
          editPromptAction(
            getPromptById?.prompt?.prompt_id,
            payload,
            setIsOpen,
            setSegmentedButtons,
            selectedSpecialty,
            selectedCondition,
            setRemovedIndices,
            setUpdatedStaticPromptPayload,
            () => setActiveSegButton(activeLLM('chat')?.description)
          )
        );
      }}
    />
  );
};

export default EditPromptPage;
